import { useCallback, useEffect, useState } from "react";

import MultiRequestQueueManager from "@/Common/Api/Services/MultiRequestQueueManager";
import { Guid } from "@/Common/Models/Guid";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { usePreferredPharmacyHelper } from "@/ContactDirectory/Hooks/usePreferredPharmacyHelper";
import { MedicalSummaryConfigurationHelper } from "@/Patient/Helpers/MedicalSummaryConfigurationHelper";
import { DisplayContextEnum } from "@/Patient/Models/MedicalNote/Enum/DisplayContextEnum";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { EncountersSidePanel } from "./Common/EncountersSidePanel";
import { MedicalNote } from "./MedicalNote/MedicalNote";
import { SecondScreenNoteScrollService } from "./MedicalNote/SecondScreenNoteScrollService";
import { MedicalSummary } from "./MedicalSummary/MedicalSummary";
import { PatientBanner } from "./PatientBanner/PatientBanner";

const noteContainerAttribute = "medical-note-main-container";
const noteIdAttribute = "note-id";
const noteListIdAttribute = "note-list-item-id";
const secondScreenBannerContainerId = "secondScreenBannerContainer";
const encountersListContainerClass = "encounter-list-main-container";
const noteAutoScrollMargin = 25;
const secondScreenNoteScrollService = new SecondScreenNoteScrollService(
    `[${noteContainerAttribute}]`,
    noteIdAttribute,
    `.${encountersListContainerClass}`,
    noteListIdAttribute,
    noteAutoScrollMargin
);
const maxCacheSize = 2;
const requestQueueManager = new MultiRequestQueueManager("fifo");

async function processEncounterLoading(encounterIds: string[], patientId: string): Promise<void> {
    const encounterIdsToProcess = encounterIds.filter(encounterId =>
        !requestQueueManager.isRequestActive(encounterId) &&
        !requestQueueManager.isRequestProcessed(encounterId)
    );

    if (encounterIdsToProcess.length > 0) {
        requestQueueManager.addRequest(() => patientStore.ActionsMedicalNote.LoadEncountersCache(encounterIdsToProcess, patientId), encounterIdsToProcess);
    }
}

interface ISecondScreenNoteProps {
    patientId: Guid;
    patientBannerModel: IPatientBannerModel;
    noteModels: IPatientMedicalNoteModel[];
    summary: IPatientMedicalSummaryModel,
    encounters: Encounter[]
}

export function SecondScreenNote(props: Readonly<ISecondScreenNoteProps>): JSX.Element {
    const [isEncounterListCollapsed, setIsEncounterListCollapsed] = useState(false);
    const [currentEncounter, setCurrentEncounter] = useState(null);
    const [encounterIdToScrollTo, setEncounterIdToScrollTo] = useState(null);

    const updateCurrentNote = useCallback((encounterId: string) => {
        if (encounterId === "") {
            setCurrentEncounter(null);
            return true;
        }
        const noteModel = props.noteModels?.firstOrDefault(note => note.get("Encounter").Id === encounterId);
        if (noteModel) {
            setCurrentEncounter(noteModel);
            return true;
        }
        return false;
    }, [props.noteModels]);

    const onEncounterClick = (encounterId: string): void => {
        const currentIndex = props.encounters.findIndex(encounter => encounter.Id === encounterId);
        patientStore.IsMedicalItemFromOutsideNote = false;

        if (updateCurrentNote(encounterId)) {
            secondScreenNoteScrollService.scrollToNote(encounterId);
            setEncounterIdToScrollTo(null);
            loadEncountersCache(props.encounters, currentIndex, false);
            return;
        }

        loadEncountersCache(props.encounters, currentIndex, true);
        setEncounterIdToScrollTo(encounterId);
    };

    const loadEncountersCache = useCallback((encounters: Encounter[], currentIndex: number, isInclusive: boolean = false) => {
        const previousEncounterIds = [];
        const nextEncounterIds = [];
        if (currentIndex > 0) {
            for (let i = currentIndex - 1; i >= 0 && i >= currentIndex - maxCacheSize; i--) {
                const currentEncounter = encounters[i];
                if (!props.noteModels?.any(note => note.get("Encounter").Id === currentEncounter.Id)) {
                    previousEncounterIds.push(currentEncounter.Id);
                    if (previousEncounterIds.length >= maxCacheSize)
                        break;
                }
            }
        }
        if (currentIndex <= encounters.length - 1) {
            for (let i = currentIndex + (isInclusive ? 0 : 1); i < encounters.length && i <= currentIndex + maxCacheSize; i++) {
                const currentEncounter = encounters[i];
                if (!props.noteModels?.any(note => note.get("Encounter").Id === currentEncounter.Id)) {
                    nextEncounterIds.push(currentEncounter.Id);
                    if (nextEncounterIds.length >= maxCacheSize)
                        break;
                }
            }
        }

        const encounterIds = previousEncounterIds.concat(nextEncounterIds);
        if (encounterIds.length) {
            processEncounterLoading(encounterIds, props.patientId);
        }
    }, [props.noteModels, props.patientId]);

    const goToTop = (): void => {
        secondScreenNoteScrollService.scrollToWindowTop();
    };

    const setHeightStyle = (): void => {
        const secondScreenPageContainer = document.getElementById("secondScreenPage");
        const secondScreenBannerContainer = document.getElementById(secondScreenBannerContainerId);

        if (secondScreenBannerContainer) {
            const bannerHeightPropertyName = "--patient-banner-height";
            const containerStyle = window.getComputedStyle(secondScreenBannerContainer);

            if (containerStyle.height != secondScreenPageContainer.style.getPropertyValue(bannerHeightPropertyName)) {
                secondScreenPageContainer.style.setProperty(bannerHeightPropertyName, containerStyle.height);
            }
        }
    };

    const getVisibleNoteModels = useCallback(
        () => props.noteModels
            ?.filter(noteModel => props.encounters.any(encounter => encounter.Id === noteModel.get("Encounter").Id)),
        [props.noteModels, props.encounters]
    );

    const onEncounterMyNotesFilterChecked = useCallback(
        (checked: boolean) => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterMyNotesFilterTermChanged, { value: checked }),
        []
    );

    const onEncounterUnapprovedNotesFilterChecked = useCallback(
        (checked: boolean) => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterUnapprovedNotesFilterTermChanged, { value: checked }),
        []
    );

    const onEncounterClinicVisitNotesFilterChecked = useCallback(
        (checked: boolean) => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterClinicVisitNotesFilterTermChanged, { value: checked }),
        []
    );

    useEffect(() => {
        if (props.encounters && !getVisibleNoteModels()?.any()) {
            loadEncountersCache(props.encounters, 0, true);
        }
        const handleNoteChange = (noteId: string): void => {
            updateCurrentNote(noteId);
            const currentIndex = props.encounters.findIndex(encounter => encounter.Id === noteId);
            loadEncountersCache(props.encounters, currentIndex);
        };

        secondScreenNoteScrollService.setOnNoteChangeCallback(noteId => handleNoteChange(noteId));
        if (encounterIdToScrollTo && props.noteModels?.any(note => note.get("Encounter").Id === encounterIdToScrollTo)) {
            secondScreenNoteScrollService.scrollToNote(encounterIdToScrollTo);
            setEncounterIdToScrollTo(null);
        }
    }, [encounterIdToScrollTo, props.noteModels, props.encounters, props.summary, updateCurrentNote, loadEncountersCache, getVisibleNoteModels]);

    const currentPatient = patientStore.getCurrentPatient();

    const preferredPharmacyHelper = usePreferredPharmacyHelper(props.patientId);

    return (
        <div className={isEncounterListCollapsed ? "collapsed" : ""}>
            <div className="go-to-top" onClick={() => goToTop()}>
                <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="21" cy="21" r="21" fill="#056AC7" />
                    <path d="M21 32V10" stroke="#F2F5F5" strokeWidth="2" strokeLinejoin="round" />
                    <path d="M12 19L21 10L30 19" stroke="#F2F5F5" strokeWidth="2" strokeLinejoin="round" />
                </svg>
            </div>
            <div className="toggle-menu-bar">
                <div onClick={() => { setIsEncounterListCollapsed(!isEncounterListCollapsed); }}>
                    <div />
                    <div />
                    <div />
                </div>
            </div>
            <div className="myle-page-header bottom-border" id={secondScreenBannerContainerId}>
                {props.patientBannerModel && (
                    <PatientBanner
                        model={props.patientBannerModel}
                        patient={currentPatient}
                        patientPortalOverview={patientStore.PatientPortalOverviewModels[props.patientId]}
                        isMSAActive={sessionStore.isMsaActive}
                        isReadOnly={true}
                        onUpdatePatientPortalInfo={() => { }}
                        onCallActionAfterUpdated={() => setHeightStyle()}
                        isCurrentEmployeeActiveSiteInBritishColumbia={sessionStore.getCurrentEmployee().getActiveSite().isSiteInProvince(CanadaState.BritishColumbia)}
                    />
                )}
            </div>
            <div className="medical-summary-container">
                {props.summary && (
                    <EncountersSidePanel
                        model={props.summary}
                        noteModel={currentEncounter}
                        isPatientNoteDisabled={true}
                        displayPrintButton={false}
                        isReadOnly={true}
                        toggleDisplayOnlyUnsigned={() => { }}
                        showCriteria={() => LocalEvent.Dispatch(LocalPatientOperations.MedicalSummary.EncounterSearchCriteriaDisplayToggle)}
                        onClickCallbackPrintNotes={() => { }}
                        newNote={() => { }}
                        onEncounterClick={encounterId => onEncounterClick(encounterId)}
                        encounterSearchTermChangedOperation={LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged}
                        fromMyleOperation={LocalPatientOperations.MedicalSummary.EncounterDateFromChanged}
                        toMyleOperation={LocalPatientOperations.MedicalSummary.EncounterDateToChanged}
                        onMyNotesFilterChanged={checked => onEncounterMyNotesFilterChecked(checked)}
                        onClinicVisitNotesFilterChanged={checked => onEncounterClinicVisitNotesFilterChecked(checked)}
                    />
                )}
            </div>
            <div>
                <MedicalSummary
                    isReadOnly
                    isHighlighting
                    isPatientNoteDisabled
                    summaryConfiguration={MedicalSummaryConfigurationHelper.getSpecificOrDefaultSummaryConfigurationGridLayout()}
                    model={props.summary}
                    currentPatient={currentPatient}
                    OnSortDoneFunction={() => { }}
                    onActiveMedicationDetailsClick={() => { }}
                    onChangeEncounterSearchTermChanged={(textOrDate: any) => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterSearchTermChanged, { "value": textOrDate })}
                    onClickCallbackPrintNotes={() => { }}
                    onConsultationRequestDeletedSuccess={() => { }}
                    onConsultationRequestSaveSuccess={() => { }}
                    onConsultationRequestToggleIsInvalid={() => { }}
                    onEncounterClick={encounterId => onEncounterClick(encounterId)}
                    onEncounterDateFromChanged={(date: Date) => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterDateFromChanged, { "value": date })}
                    onEncounterDateToChanged={(date: Date) => sessionStore.dispatch(LocalPatientOperations.MedicalSummary.EncounterDateToChanged, { "value": date })}
                    onLabRequestSaveSuccess={() => { }}
                    onLabRequestToggleIsInvalid={() => { }}
                    onMedicalItemClick={() => { }}
                    onNewNote={() => { }}
                    onRequestDeletedSuccess={() => { }}
                    onSaveObstetricalSummary={() => { }}
                    onShowCriteria={() => LocalEvent.Dispatch(LocalPatientOperations.MedicalSummary.EncounterSearchCriteriaDisplayToggle)}
                    onSwitchView={() => { }}
                    onToggleDisplayOnlyUnsigned={() => { }}
                    onUpdateScreeningsModelAfterPrintingRequest={() => { }}
                    onEncounterMyNotesFilterChecked={checked => onEncounterMyNotesFilterChecked(checked)}
                    onEncounterUnapprovedNotesFilterChecked={checked => onEncounterUnapprovedNotesFilterChecked(checked)}
                    onEncounterClinicVisitNotesFilterChecked={checked => onEncounterClinicVisitNotesFilterChecked(checked)}
                />
            </div>
            {!IsNullOrUndefined(currentPatient?.IndividualResponse?.Contact_id)
                && props.patientId == patientStore.getCurrentPatientContactId() // MedicalNote still refers to the active patient instead of PatientUIModel. It should not
                && getVisibleNoteModels()?.map(noteModel => (
                    <>
                        <div className="notes-separator" />
                        <MedicalNote
                            key={`medicalNote${noteModel.get("Encounter").Id}`}
                            PatientUIModel={patientStore.PatientUIModels[props.patientId]}
                            ClinicianModel={patientStore.PatientProfileCliniciansModels[props.patientId]}
                            PatientId={props.patientId}
                            NoteModel={noteModel}
                            MedicalSummaryModel={props.summary}
                            PreferredPharmacy={currentPatient?.PreferredPharmacy?.getPharmacyInfo(true, preferredPharmacyHelper)}
                            PatientModel={currentPatient}
                            onEncounterClick={encounterId => onEncounterClick(encounterId)}
                            onEncounterMyNotesFilterChecked={checked => onEncounterMyNotesFilterChecked(checked)}
                            onEncounterUnapprovedNotesFilterChecked={checked => onEncounterUnapprovedNotesFilterChecked(checked)}
                            onEncounterClinicVisitNotesFilterChecked={checked => onEncounterClinicVisitNotesFilterChecked(checked)}
                            isReadOnly={true}
                            isMedicalSummaryCollapsed
                            displayContext={DisplayContextEnum.SecondScreen}
                        />
                    </>
                ))
            }
        </div>
    );
}
